import {
  CardSelectionType,
  SelectableCard,
  colors,
  fontWeights,
  sizing,
  getTestAttributes,
} from '@chillibean/component-library';
import { Accordion } from '@chillibean/component-library/legacy';
import pluralize from 'pluralize';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { Site } from '../../../../models';

export function SitesAccordion(props) {
  const { baseUrl, numberOfAllSites = 0, sites = [] } = props;

  const getPluralizedSiteText = (number) => pluralize('Site', number);
  const getTooltipText = (studyProtocolName) => `This site is being used in ${studyProtocolName}`;

  const renderSitesHeader = (numberOfSites, studyProtocol) => {
    return (
      <SitesHeader {...getTestAttributes('site-group-header')}>
        {numberOfSites} {getPluralizedSiteText(numberOfSites)} currently used in{' '}
        <Link to={`${baseUrl}/${studyProtocol.id}/sites`}>{studyProtocol.name}</Link>
      </SitesHeader>
    );
  };

  const renderAccordionItem = () => {
    const studyProtocols = [...new Map(sites.map((site) => [site.studyProtocol.id, site.studyProtocol])).values()];
    const singleStudyProtocol = studyProtocols.length === 1;
    const suffix = singleStudyProtocol ? (
      <Link to={`${baseUrl}/${studyProtocols[0].id}/sites`}>{studyProtocols[0].name}</Link>
    ) : (
      'multiple trial configurations'
    );

    return (
      <AccordionItem {...getTestAttributes('sites-accordion-header')}>
        {sites.length}/{numberOfAllSites} {getPluralizedSiteText(sites.length)} currently used in {suffix}
      </AccordionItem>
    );
  };

  const renderAccordionContent = () => {
    const studyProtocols = [...new Map(sites.map((site) => [site.studyProtocol.id, site.studyProtocol])).values()];

    return (
      <AccordionContent>
        {studyProtocols.length === 1 && (
          <Sites>
            {sites.map((site) => (
              <SelectableCard
                key={site.id}
                details={site.getDetails()}
                disabled
                selected
                title={site.name}
                tooltip={getTooltipText(studyProtocols[0].name)}
                type={CardSelectionType.MULTI}
                value={site.id}
              />
            ))}
          </Sites>
        )}
        {studyProtocols.length > 1 &&
          studyProtocols.map((studyProtocol) => {
            const protocolSites = sites.filter((site) => site.studyProtocol.id === studyProtocol.id);

            return (
              <SitesGroup key={studyProtocol.id}>
                {renderSitesHeader(protocolSites.length, studyProtocol)}
                <Sites>
                  {protocolSites.map((site) => (
                    <SelectableCard
                      key={site.id}
                      details={site.getDetails()}
                      disabled
                      selected
                      title={site.name}
                      tooltip={getTooltipText(studyProtocol.name)}
                      type={CardSelectionType.MULTI}
                      value={site.id}
                    />
                  ))}
                </Sites>
              </SitesGroup>
            );
          })}
      </AccordionContent>
    );
  };

  return (
    <StyledAccordion>
      <Accordion content={renderAccordionContent()} id="sites-accordion" item={renderAccordionItem()} />
    </StyledAccordion>
  );
}

const StyledAccordion = styled.div`
  padding-right: ${sizing.xlarge};

  border-radius: ${sizing.xsmall};
  border: 1px solid ${colors.grey.medium};
`;

const AccordionItem = styled.div`
  padding-top: ${sizing.large};
  padding-bottom: ${sizing.large};
  padding-left: ${sizing.xlarge};
`;

const AccordionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizing.xlarge};

  padding-bottom: ${sizing.large};
  padding-left: ${sizing.xlarge};
`;

const SitesGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizing.medium};
`;

const SitesHeader = styled.div`
  font-weight: ${fontWeights.bold};

  a {
    color: ${colors.grey.medium};
    font-weight: ${fontWeights.bold};
    text-decoration: underline;
  }
`;

const Sites = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizing.medium};
`;

SitesAccordion.propTypes = {
  baseUrl: PropTypes.string.isRequired,
  numberOfAllSites: PropTypes.number,
  sites: PropTypes.arrayOf(PropTypes.instanceOf(Site)),
};

export default SitesAccordion;
